import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { MapPin, Star, TrendingUp, MessageSquare, Calendar, CheckCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GBPOptimization = () => {

  return (
    <>
      <SEO 
        title="Google Business Profile for Lawn Care & Landscaping | Get More Clients"
        description="Dominate local search with Google Business Profile optimization for lawn care & landscaping. Rank higher, get more calls, and win more clients."
        keywords="google business profile lawn care, GBP landscaping, local SEO lawn care, GBP optimization"
        canonical="https://www.leafrushmarketing.com/growth-add-ons/gbp/"
        ogUrl="https://www.leafrushmarketing.com/growth-add-ons/gbp/"
      />
      <div className="flex flex-col">
      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <Badge variant="secondary" className="mb-4">
              Local SEO for Lawn Care & Landscaping
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Google Business Profile Optimization for{" "}
              <span className="text-primary">Lawn Care & Landscaping</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Dominate the local map pack and get more calls without spending on ads. 
              We optimize your Google Business Profile to capture homeowners searching in your area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/contact#get-started">
                <Button size="lg">Get Started</Button>
              </NavLink>
              <NavLink to="/websites">
                <Button size="lg" variant="outline">View Our Work</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What You Get</h2>
            <p className="text-xl text-muted-foreground">
              Complete GBP management to maximize your local visibility
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2">
              <CardHeader>
                <Star className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Review Generation & Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Automated review requests after each job</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Professional responses to all reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Reputation monitoring and alerts</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <MapPin className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Map Pack Ranking Optimization</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Strategic category & service optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Geo-signal strengthening for your service area</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Local citation building and management</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <Calendar className="h-10 w-10 text-primary mb-4" />
                <CardTitle>Monthly Posts & Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Weekly Google Posts with seasonal content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Profile photo and video optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Q&A section management</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How GBP Works With Your Website Design */}
      <section className="py-12 bg-primary/5">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">How GBP Works With Your Website Design</h2>
            <p className="text-muted-foreground mb-4">
              Your lawn care website design includes local SEO structure that aligns with your Google Business Profile. When both work together, you dominate local search results.
            </p>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Service pages</strong> on your website match the services listed on your GBP</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Service area pages</strong> reinforce your GBP service areas with dedicated content</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong>Consistent NAP</strong> (name, address, phone) across website and GBP builds trust with Google</span>
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-6">
              Need a website first? See our <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink> and <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> packages.
            </p>
          </div>
        </div>
      </section>

      {/* Why GBP Matters */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why GBP Matters for Lawn Care</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Most homeowners start their search on Google Maps when they need lawn care or landscaping services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Local Intent</h3>
                  <p className="text-muted-foreground">
                    Searches like "lawn care near me" and "landscaping company" show the map pack first. If you're not there, you're invisible.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Star className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Trust Building</h3>
                  <p className="text-muted-foreground">
                    Reviews and ratings are the #1 factor homeowners use to evaluate lawn care companies. A strong GBP profile builds instant credibility.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Seasonal Opportunity</h3>
                  <p className="text-muted-foreground">
                    When spring hits, optimized profiles capture the surge of homeowners searching for lawn care services.
                  </p>
                </div>
              </div>
            </div>

            <Card className="border-2 bg-accent/30">
              <CardHeader>
                <CardTitle>By the Numbers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center p-6 bg-card rounded-lg">
                  <p className="text-4xl font-bold text-primary mb-2">76%</p>
                  <p className="text-sm text-muted-foreground">
                    of people who search for something nearby visit a business within 24 hours
                  </p>
                </div>
                <div className="text-center p-6 bg-card rounded-lg">
                  <p className="text-4xl font-bold text-primary mb-2">28%</p>
                  <p className="text-sm text-muted-foreground">
                    of local searches result in a purchase within 24 hours
                  </p>
                </div>
                <div className="text-center p-6 bg-card rounded-lg">
                  <p className="text-4xl font-bold text-primary mb-2">5x</p>
                  <p className="text-sm text-muted-foreground">
                    more likely to consider a business with a complete GBP profile
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monthly Process */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Monthly Process</h2>
            <p className="text-xl text-muted-foreground">
              Consistent optimization that compounds over time
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                1
              </div>
              <h3 className="font-semibold text-lg">Audit & Foundation</h3>
              <p className="text-muted-foreground text-sm">
                Review current profile status, fix errors, optimize categories and services
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                2
              </div>
              <h3 className="font-semibold text-lg">Review Engine</h3>
              <p className="text-muted-foreground text-sm">
                Set up automated review requests, respond to all reviews professionally
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                3
              </div>
              <h3 className="font-semibold text-lg">Content & Posts</h3>
              <p className="text-muted-foreground text-sm">
                Publish weekly Google Posts, optimize photos, manage Q&A section
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                4
              </div>
              <h3 className="font-semibold text-lg">Track & Report</h3>
              <p className="text-muted-foreground text-sm">
                Monitor insights, track ranking changes, provide monthly performance reports
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Expected Outcomes</h2>
            <p className="text-xl text-muted-foreground">
              What you can expect within 3-6 months of GBP optimization
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-xl text-center mb-2">More Calls</h3>
                <p className="text-center text-muted-foreground">
                  2-3x increase in phone calls from Google Maps and Search
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-xl text-center mb-2">Direction Requests</h3>
                <p className="text-center text-muted-foreground">
                  50-100% increase in homeowners getting directions to your business
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-xl text-center mb-2">Profile Views</h3>
                <p className="text-center text-muted-foreground">
                  Consistent growth in profile views and search appearances
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Get Started */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">Ready to Dominate Local Search?</h2>
            <p className="text-xl text-muted-foreground">
              Let's discuss your market, goals, and create a custom strategy for your business.
            </p>
            <p className="text-lg text-muted-foreground">
              Custom pricing based on your market and goals — book a strategy call to get started.
            </p>
            <NavLink to="/contact">
              <Button size="lg" className="text-lg px-8">
                Book a Strategy Call
              </Button>
            </NavLink>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="max-w-3xl mx-auto">
            <AccordionItem value="item-1">
              <AccordionTrigger>How long before I see results?</AccordionTrigger>
              <AccordionContent>
                Most clients see increased profile views and calls within the first month. However, significant map pack ranking improvements typically take 3-6 months as we build review volume and optimize signals.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>Do I need access to my Google Business Profile?</AccordionTrigger>
              <AccordionContent>
                Yes, you'll need to grant us manager access to your GBP. We'll walk you through the simple process. If you don't have a GBP yet, we'll help you create and verify one.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>Will this work if I have negative reviews?</AccordionTrigger>
              <AccordionContent>
                Yes. We'll respond professionally to all reviews (including negative ones) and implement a review generation system to overwhelm negative reviews with positive ones over time. Most businesses see their rating improve within 2-3 months.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger>Can I do this myself?</AccordionTrigger>
              <AccordionContent>
                Technically yes, but it requires consistent effort, technical SEO knowledge, and staying updated on Google's algorithm changes. Most business owners lack the time or expertise. We handle everything so you can focus on running your business.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>What if I already rank well locally?</AccordionTrigger>
              <AccordionContent>
                There's always room for improvement. We can help you maintain your rankings, expand into new service areas, and optimize for more specific service keywords. We'll also ensure you're maximizing clicks and calls from your existing visibility.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>Is there a contract?</AccordionTrigger>
              <AccordionContent>
                No long-term contracts. Month-to-month service that you can cancel anytime. However, we recommend staying for at least 6 months to see the full compound effect of GBP optimization.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Calendly Scheduling */}
      <CalendlyEmbed />
      </div>
    </>
  );
};

export default GBPOptimization;
